import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Alert,
  Snackbar,
  Avatar,
  IconButton,
  Divider,
  InputAdornment,
} from '@mui/material';
import CloudUpload from '@mui/icons-material/CloudUpload';
import Delete from '@mui/icons-material/Delete';
import Save from '@mui/icons-material/Save';
import Refresh from '@mui/icons-material/Refresh';
import School from '@mui/icons-material/School';
import Palette from '@mui/icons-material/Palette';
import Image from '@mui/icons-material/Image';
import Percent from '@mui/icons-material/Percent';
import { useSchoolSettings } from '../../contexts/SchoolSettingsContext';

const colorPresets = [
  { name: 'Teal Ocean', start: '#0d9488', end: '#0891b2' },
  { name: 'Forest Green', start: '#15803d', end: '#22c55e' },
  { name: 'Ocean Blue', start: '#1e40af', end: '#3b82f6' },
  { name: 'Sunset Orange', start: '#c2410c', end: '#f97316' },
  { name: 'Rose Pink', start: '#be185d', end: '#ec4899' },
  { name: 'Slate Gray', start: '#334155', end: '#64748b' },
  { name: 'Golden Amber', start: '#b45309', end: '#f59e0b' },
  { name: 'Deep Navy', start: '#0f172a', end: '#1e293b' },
];

export const SchoolSettingsManagement: React.FC = () => {
  const { settings, updateSettings, resetSettings } = useSchoolSettings();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [schoolName, setSchoolName] = useState(settings.schoolName);
  const [logoUrl, setLogoUrl] = useState<string | null>(settings.logoUrl);
  const [gradientStartColor, setGradientStartColor] = useState(settings.gradientStartColor);
  const [gradientEndColor, setGradientEndColor] = useState(settings.gradientEndColor);
  // Stored on settings as a decimal (0.07); the input shows
  // percent-friendly numbers (7) so admins don't have to think in
  // decimals.
  const [taxRatePercent, setTaxRatePercent] = useState(
    (settings.taxRate * 100).toFixed(2).replace(/\.?0+$/, ''),
  );
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  useEffect(() => {
    setSchoolName(settings.schoolName);
    setLogoUrl(settings.logoUrl);
    setGradientStartColor(settings.gradientStartColor);
    setGradientEndColor(settings.gradientEndColor);
    setTaxRatePercent((settings.taxRate * 100).toFixed(2).replace(/\.?0+$/, ''));
  }, [settings]);

  const handleLogoUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setSnackbar({ open: true, message: 'Logo file size must be less than 5MB', severity: 'error' });
        return;
      }
      if (!file.type.startsWith('image/')) {
        setSnackbar({ open: true, message: 'Please upload an image file', severity: 'error' });
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoUrl(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveLogo = () => {
    setLogoUrl(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handlePresetSelect = (preset: { start: string; end: string }) => {
    setGradientStartColor(preset.start);
    setGradientEndColor(preset.end);
  };

  const handleSave = () => {
    if (!schoolName.trim()) {
      setSnackbar({ open: true, message: 'School name is required', severity: 'error' });
      return;
    }
    const parsedRate = parseFloat(taxRatePercent);
    if (isNaN(parsedRate) || parsedRate < 0 || parsedRate > 100) {
      setSnackbar({ open: true, message: 'Tax rate must be between 0% and 100%', severity: 'error' });
      return;
    }
    updateSettings({
      schoolName: schoolName.trim(),
      logoUrl,
      gradientStartColor,
      gradientEndColor,
      taxRate: parsedRate / 100,
    });
    setSnackbar({ open: true, message: 'School settings saved successfully', severity: 'success' });
  };

  const handleReset = () => {
    resetSettings();
    setSnackbar({ open: true, message: 'Settings reset to defaults', severity: 'success' });
  };

  const gradientStyle = `linear-gradient(135deg, ${gradientStartColor} 0%, ${gradientEndColor} 100%)`;

  return (
    <Box>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" fontWeight={700} gutterBottom>
          Site Settings
        </Typography>
        <Typography color="text.secondary">
          Configure your school's branding, logo, color scheme, and tax rate
        </Typography>
      </Box>

      <Grid container spacing={3}>
        <Grid size={{ xs: 12, lg: 8 }}>
          <Paper sx={{ p: 4, mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
              <School sx={{ color: 'primary.main', fontSize: 28 }} />
              <Typography variant="h6" fontWeight={600}>
                School Information
              </Typography>
            </Box>
            <TextField
              fullWidth
              label="School Name"
              value={schoolName}
              onChange={(e) => setSchoolName(e.target.value)}
              placeholder="Enter your school name"
              helperText="This name will be displayed throughout the application"
            />
          </Paper>

          <Paper sx={{ p: 4, mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
              <Image sx={{ color: 'primary.main', fontSize: 28 }} />
              <Typography variant="h6" fontWeight={600}>
                School Logo
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
              <Avatar
                src={logoUrl || undefined}
                sx={{
                  width: 120,
                  height: 120,
                  bgcolor: 'grey.100',
                  border: '2px dashed',
                  borderColor: logoUrl ? 'primary.main' : 'grey.300',
                }}
              >
                {!logoUrl && <School sx={{ fontSize: 48, color: 'grey.400' }} />}
              </Avatar>

              <Box sx={{ flex: 1 }}>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleLogoUpload}
                  style={{ display: 'none' }}
                  id="logo-upload"
                />
                <label htmlFor="logo-upload">
                  <Button
                    variant="outlined"
                    component="span"
                    startIcon={<CloudUpload />}
                    sx={{ mb: 1 }}
                  >
                    Upload Logo
                  </Button>
                </label>
                {logoUrl && (
                  <Button
                    variant="text"
                    color="error"
                    startIcon={<Delete />}
                    onClick={handleRemoveLogo}
                    sx={{ ml: 1, mb: 1 }}
                  >
                    Remove
                  </Button>
                )}
                <Typography variant="body2" color="text.secondary">
                  Recommended: Square image, at least 200x200px. Max file size: 5MB
                </Typography>
              </Box>
            </Box>
          </Paper>

          <Paper sx={{ p: 4, mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
              <Palette sx={{ color: 'primary.main', fontSize: 28 }} />
              <Typography variant="h6" fontWeight={600}>
                Color Scheme
              </Typography>
            </Box>

            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
              Preset Color Schemes
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 4 }}>
              {colorPresets.map((preset) => (
                <Box
                  key={preset.name}
                  onClick={() => handlePresetSelect(preset)}
                  sx={{
                    width: 100,
                    cursor: 'pointer',
                    borderRadius: 2,
                    overflow: 'hidden',
                    border: '2px solid',
                    borderColor:
                      gradientStartColor === preset.start && gradientEndColor === preset.end
                        ? 'primary.main'
                        : 'transparent',
                    transition: 'all 0.2s',
                    '&:hover': {
                      transform: 'scale(1.05)',
                      boxShadow: 2,
                    },
                  }}
                >
                  <Box
                    sx={{
                      height: 40,
                      background: `linear-gradient(135deg, ${preset.start} 0%, ${preset.end} 100%)`,
                    }}
                  />
                  <Typography
                    variant="caption"
                    sx={{
                      display: 'block',
                      textAlign: 'center',
                      py: 0.5,
                      px: 0.5,
                      bgcolor: 'grey.50',
                      fontWeight: 500,
                      fontSize: '0.7rem',
                    }}
                  >
                    {preset.name}
                  </Typography>
                </Box>
              ))}
            </Box>

            <Divider sx={{ my: 3 }} />

            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
              Custom Colors
            </Typography>
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
                  Gradient Start Color
                </Typography>
                <Box sx={{ display: 'flex', gap: 1.5 }}>
                  <Box
                    sx={{
                      width: 56,
                      height: 56,
                      borderRadius: 2,
                      bgcolor: gradientStartColor,
                      border: '2px solid',
                      borderColor: 'grey.200',
                      position: 'relative',
                      overflow: 'hidden',
                      cursor: 'pointer',
                    }}
                  >
                    <input
                      type="color"
                      value={gradientStartColor}
                      onChange={(e) => setGradientStartColor(e.target.value)}
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        opacity: 0,
                        cursor: 'pointer',
                      }}
                    />
                  </Box>
                  <TextField
                    size="small"
                    value={gradientStartColor}
                    onChange={(e) => setGradientStartColor(e.target.value)}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">#</InputAdornment>,
                    }}
                    sx={{ flex: 1 }}
                    inputProps={{
                      style: { fontFamily: 'monospace' },
                    }}
                  />
                </Box>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
                  Gradient End Color
                </Typography>
                <Box sx={{ display: 'flex', gap: 1.5 }}>
                  <Box
                    sx={{
                      width: 56,
                      height: 56,
                      borderRadius: 2,
                      bgcolor: gradientEndColor,
                      border: '2px solid',
                      borderColor: 'grey.200',
                      position: 'relative',
                      overflow: 'hidden',
                      cursor: 'pointer',
                    }}
                  >
                    <input
                      type="color"
                      value={gradientEndColor}
                      onChange={(e) => setGradientEndColor(e.target.value)}
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        opacity: 0,
                        cursor: 'pointer',
                      }}
                    />
                  </Box>
                  <TextField
                    size="small"
                    value={gradientEndColor}
                    onChange={(e) => setGradientEndColor(e.target.value)}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">#</InputAdornment>,
                    }}
                    sx={{ flex: 1 }}
                    inputProps={{
                      style: { fontFamily: 'monospace' },
                    }}
                  />
                </Box>
              </Grid>
            </Grid>
          </Paper>

          <Paper sx={{ p: 4, mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
              <Percent sx={{ color: 'primary.main', fontSize: 28 }} />
              <Typography variant="h6" fontWeight={600}>
                Sales Tax
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Sales tax is charged only on fees flagged <strong>Taxable</strong> in
              Fees Management — typically concession-stand items and merchandise.
              Educational fees stay tax-exempt regardless of this rate.
            </Typography>
            <TextField
              label="Tax Rate"
              type="number"
              value={taxRatePercent}
              onChange={(e) => setTaxRatePercent(e.target.value)}
              placeholder="e.g. 7"
              helperText="Applied to taxable fees at checkout. Set to 0 to disable."
              InputProps={{
                endAdornment: <InputAdornment position="end">%</InputAdornment>,
                inputProps: { min: 0, max: 100, step: 0.01 },
              }}
              sx={{ maxWidth: 240 }}
            />
          </Paper>
        </Grid>

        <Grid size={{ xs: 12, lg: 4 }}>
          <Paper sx={{ p: 3, mb: 3, position: 'sticky', top: 88 }}>
            <Typography variant="h6" fontWeight={600} sx={{ mb: 3 }}>
              Preview
            </Typography>

            <Box
              sx={{
                borderRadius: 2,
                overflow: 'hidden',
                border: '1px solid',
                borderColor: 'grey.200',
                mb: 3,
              }}
            >
              <Box
                sx={{
                  background: gradientStyle,
                  p: 2,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                }}
              >
                {logoUrl ? (
                  <Avatar src={logoUrl} sx={{ width: 36, height: 36 }} />
                ) : (
                  <School sx={{ color: 'white', fontSize: 28 }} />
                )}
                <Typography
                  sx={{
                    color: 'white',
                    fontWeight: 600,
                    fontSize: '0.9rem',
                    textShadow: '0 1px 2px rgba(0,0,0,0.2)',
                  }}
                >
                  {schoolName || 'School Name'}
                </Typography>
              </Box>
              <Box sx={{ p: 2, bgcolor: 'grey.50' }}>
                <Typography variant="body2" color="text.secondary">
                  This is how your header will appear across the application
                </Typography>
              </Box>
            </Box>

            <Box
              sx={{
                p: 2,
                borderRadius: 2,
                background: gradientStyle,
                mb: 3,
              }}
            >
              <Typography variant="body2" sx={{ color: 'white', fontWeight: 500, mb: 1 }}>
                Gradient Preview
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: 'rgba(255,255,255,0.8)', fontFamily: 'monospace' }}
              >
                {gradientStartColor} → {gradientEndColor}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="contained"
                startIcon={<Save />}
                onClick={handleSave}
                fullWidth
                size="large"
              >
                Save Changes
              </Button>
              <IconButton
                onClick={handleReset}
                sx={{
                  border: '1px solid',
                  borderColor: 'grey.300',
                  borderRadius: 2,
                }}
                title="Reset to defaults"
              >
                <Refresh />
              </IconButton>
            </Box>
          </Paper>

          <Alert severity="info">
            Changes to the color scheme will be applied immediately across the entire application.
          </Alert>
        </Grid>
      </Grid>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};
